/**
 * Fase B / B4 — Pedido congelado (DEMO-03, dados.ts: congelarPedido,
 * economiaPedido; indicadores.ts: itensDoGrupo). O Pedido nasce da VERSÃO
 * ACEITA e só das quantidades GANHAS, sem redigitação: cliente, entidade,
 * faturamento, entrega, itens, custos, preços de venda, frete, condições, PTAX
 * e abatimento ficam congelados. Locação e Trade-in guardam os dados da
 * operação (preço fechado / avaliação) e os componentes ganhos.
 */
import type { Reajuste } from './comercial.ts';
import type { RegraRecebimentoCongelada } from './regra-recebimento.ts';
import {
  arred,
  avaliacaoSelecionada,
  calcularLocacao,
  calcularTradein,
  comNome,
  comReajuste,
  copiaProduto,
  custoItemReais,
  economia,
  fimEstimado,
  localParqueDaVersao,
  locaisDaVersao,
  normalizarFrete,
  ofertaSelecionada,
  produtoDoItem,
  reajusteDaLocacao,
  type AbatimentoCongelado,
  type AvaliacaoTradein,
  type Base,
  type BaseComissao,
  type ComponenteOperacao,
  type CondicoesLocacaoRef,
  type CondicoesVersao,
  type ConfirmacaoBancaria,
  type DadosEmpresa,
  type DadosTradein,
  type Frete,
  type LocalCongelado,
  type OfertaLocacao,
  type ProdutoCopia,
  type RegraComissaoAplicada,
  type Tipo,
  type Versao,
} from './propostas.ts';

export const TIPOS_OC = ['inicial', 'complementar', 'revisada', 'substituta'] as const;
export type TipoOc = (typeof TIPOS_OC)[number];
export const NOMES_TIPO_OC: Record<TipoOc, string> = { inicial: 'Inicial', complementar: 'Complementar', revisada: 'Revisada', substituta: 'Substituta' };

export interface ItemPedido {
  itemId: string;
  produtoId: string;
  quantidade: number;
  precoUnit: number;
  custoUnit: number; // R$
  entidadeId: string;
  disponibilidade: string;
  prazoDias: number;
  ptax?: number;
  produtoCopia?: ProdutoCopia;
}
export type LocacaoPedido = Omit<OfertaLocacao, 'id' | 'validadeDias' | 'observacao'> &
  ReturnType<typeof calcularLocacao> & {
    base: BaseComissao;
    descricao: string;
    dataInicial: string;
    dataFinal: string;
    vencimentoInicial: string;
    diaCobranca: number;
    formaPagamento: string;
    componentes: ComponenteOperacao[];
    condicoes?: CondicoesLocacaoRef;
    regraComissao?: RegraComissaoAplicada;
    dataImplantacao?: string;
    responsavelEntrega?: string;
    responsavelImplantacao?: string;
    observacoes?: string;
  };
export type TradeinPedido = Omit<AvaliacaoTradein, 'id' | 'validadeDias' | 'linhas'> &
  ReturnType<typeof calcularTradein> &
  Omit<DadosTradein, 'equipamentos' | 'avaliacoes' | 'selecionadaId'> & {
    equipamentos: ComponenteOperacao[];
    localCongelado?: { texto: string; origem: string };
    confirmacaoBancaria?: ConfirmacaoBancaria;
  };
/** Documento congelado do Pedido (formato da DEMO-03). */
export interface ConteudoPedido {
  tipo: Tipo;
  faturamento: LocalCongelado;
  entrega: LocalCongelado;
  frete: Frete;
  condicoes: CondicoesVersao;
  ptax: number;
  itens: ItemPedido[];
  abatimento?: AbatimentoCongelado;
  empresa?: DadosEmpresa | null;
  locacao?: LocacaoPedido;
  tradein?: TradeinPedido;
  /** Parametrização: regra de recebimento da comissão copiada no envio da versão aceita (ausente em Pedidos anteriores) */
  recebimentoParceiros?: Record<string, RegraRecebimentoCongelada>;
}

const CIF: Frete = { modalidade: 'CIF', pagador: 'incluso', valor: 0 };

/**
 * Congela o Pedido a partir da versão ACEITA (já com o resultado por linha) e das quantidades GANHAS.
 * `ganhos`: linha → quantidade ganha. Locação e Trade-in: uma linha econômica sintética representa a operação.
 */
export function congelarPedido(d: Pick<Base, 'produtos' | 'clientes'>, v: Versao, ganhos: { itemId: string; quantidade: number }[]): ConteudoPedido {
  const { faturamento, entrega } = locaisDaVersao(d, v);
  const base = { faturamento, entrega, condicoes: v.condicoes, ptax: v.ptax, abatimento: v.abatimento, empresa: v.empresa ?? null, ...(v.recebimentoParceiros ? { recebimentoParceiros: v.recebimentoParceiros } : {}) };
  const ganhosDe = (cs: ComponenteOperacao[]) => cs.map((c) => ({ ...c, quantidadeAceita: Math.min(ganhos.find((g) => g.itemId === c.id)?.quantidade ?? 0, c.quantidade) })).filter((c) => c.quantidadeAceita > 0);
  if (v.locacao) {
    const o = ofertaSelecionada(v)!;
    const c = calcularLocacao(o, v.locacao.base);
    const { id: _i, validadeDias: _v, observacao: _o, ...oferta } = o;
    const loc = v.locacao;
    const reajuste: Reajuste | undefined = reajusteDaLocacao(loc);
    return {
      tipo: 'locacao',
      ...base,
      frete: CIF,
      itens: [{ itemId: 'operacao', produtoId: '', quantidade: 1, precoUnit: c.totalContrato, custoUnit: c.liquidoTotal, entidadeId: o.entidadeId, disponibilidade: loc.implantacao, prazoDias: 0 }],
      locacao: {
        ...oferta,
        ...c,
        base: loc.base,
        descricao: loc.descricao,
        dataInicial: loc.dataInicial,
        // P3-27: sem início efetivo no rascunho, o término também fica para a OL
        dataFinal: loc.dataInicial ? fimEstimado(loc.dataInicial, o.prazoMeses) : '',
        vencimentoInicial: loc.vencimentoInicial,
        diaCobranca: loc.diaCobranca,
        formaPagamento: loc.formaPagamento,
        componentes: ganhosDe(loc.componentes),
        condicoes: loc.condicoes ? { ...loc.condicoes } : undefined,
        ...(reajuste ? comReajuste(reajuste) : {}),
        regraComissao: loc.regraComissao ? { ...loc.regraComissao, historico: [...loc.regraComissao.historico] } : undefined,
        dataImplantacao: loc.dataImplantacao,
        responsavelEntrega: loc.responsavelEntrega,
        responsavelImplantacao: loc.responsavelImplantacao,
        observacoes: loc.observacoes,
      },
    };
  }
  if (v.tradein) {
    const a = avaliacaoSelecionada(v)!;
    const { id: _i, validadeDias: _v, linhas: _l, ...aval } = a;
    const { equipamentos, avaliacoes: _a, selecionadaId: _s, ...tri } = v.tradein;
    // Trade-in: totais recalculados SÓ sobre as quantidades GANHAS de cada linha.
    const ganhosEq = ganhosDe(equipamentos);
    const c = calcularTradein(a, comNome(ganhosEq).map((e) => ({ ...e, quantidade: e.quantidadeAceita })));
    const local = localParqueDaVersao(d, v);
    return {
      tipo: 'trade_in',
      ...base,
      frete: CIF,
      itens: [{ itemId: 'operacao', produtoId: '', quantidade: 1, precoUnit: c.bruto, custoUnit: c.oferta, entidadeId: a.entidadeId, disponibilidade: tri.retirada, prazoDias: a.prazoRetiradaDias }],
      tradein: { ...aval, ...c, ...tri, equipamentos: ganhosEq, localCongelado: { texto: local.texto, origem: local.origem } },
    };
  }
  const itens: ItemPedido[] = ganhos
    .map((g) => {
      const i = v.itens.find((x) => x.id === g.itemId)!;
      const pr = produtoDoItem(d, i);
      const cadastro = d.produtos.find((p) => p.id === i.produtoId);
      return { itemId: i.id, produtoId: i.produtoId, quantidade: Math.min(g.quantidade, i.quantidade), precoUnit: i.precoUnit, custoUnit: custoItemReais(i, d, v.ptax), entidadeId: i.entidadeId, disponibilidade: i.disponibilidade, prazoDias: i.prazoDias, produtoCopia: i.produtoCopia ?? (cadastro ? copiaProduto(cadastro) : pr), ...(pr.moeda === 'USD' ? { ptax: i.ptaxUtilizada ?? v.ptax } : {}) };
    })
    .filter((i) => i.quantidade > 0);
  return { tipo: 'venda', ...base, frete: normalizarFrete(v.frete), itens };
}

/** Resumo econômico do Pedido (mesmos cenários de frete da Proposta). */
export const economiaPedido = (p: Pick<ConteudoPedido, 'itens' | 'frete'>) => economia(p.itens.map((i) => ({ quantidade: i.quantidade, custoUnit: i.custoUnit, vendaUnit: i.precoUnit })), p.frete);

/** Itens do Pedido que contam para o parceiro filtrado ('' = todos; 'sem' = sem entidade faturadora). */
export function itensDoGrupo(d: Pick<Base, 'entidades'>, p: Pick<ConteudoPedido, 'itens'>, grupo: string) {
  return p.itens.filter((i) => !grupo || (grupo === 'sem' ? !i.entidadeId : i.entidadeId && d.entidades.find((e) => e.id === i.entidadeId)?.grupoId === grupo));
}

/** Totais e resumo por parceiro gravados com o Pedido (listas, filtros e indicadores). */
export function totaisPedido(d: Pick<Base, 'entidades'>, p: ConteudoPedido) {
  const e = economiaPedido(p);
  const grupos = [...new Set(p.itens.map((i) => d.entidades.find((x) => x.id === i.entidadeId)?.grupoId).filter((x): x is string => Boolean(x)))];
  const por_grupo = Object.fromEntries(grupos.map((g) => [g, arred(economiaPedido({ ...p, itens: itensDoGrupo(d, p, g), frete: CIF }).venda)]));
  return { valor_vendido: e.venda, custo_total: e.custo, margem_bruta: e.bruta, margem_percentual: Number(e.percentual.toFixed(6)), a_faturar: e.aFaturar, resumo: { grupos, por_grupo } };
}

/**
 * Situação do Pedido. Venda: calculada pelo faturamento das Ordens válidas (B5); sem Ordens ainda,
 * "Em andamento". Locação e Trade-in: "Em andamento" (situação ilustrativa até os documentos operacionais).
 */
export type SituacaoPedido = 'Em andamento' | 'Parcialmente cancelado' | 'Faturado parcialmente' | 'Faturado parcialmente · saldo cancelado' | 'Faturado' | 'Cancelado';
